import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaEdit,
  FaPaperclip,
  FaPlayCircle,
  FaRegFileAlt,
  FaTimes,
  FaTools,
  FaBookOpen,
} from 'react-icons/fa';
import { graphqlRequest } from '../../utils/graphqlRequest';
import {
  CREATE_SUPPORT_KB_ARTICLE_MUTATION,
  SUPPORT_KB_ARTICLES_PUBLISHED_QUERY,
  UPDATE_SUPPORT_KB_ARTICLE_MUTATION,
} from '../../graphql/supportKnowledgeBase';
import '../../styles/AdminKnowledgeBase.css';

const ARTICLE_TYPE_MAP = {
  FAQ: { label: 'FAQ Articles', icon: FaRegFileAlt, dotColor: '#7c3aed' },
  GUIDE: { label: 'Guides', icon: FaBookOpen, dotColor: '#1570ef' },
  TUTORIAL: { label: 'Tutorials', icon: FaPlayCircle, dotColor: '#079455' },
  TROUBLESHOOTING: { label: 'Troubleshooting', icon: FaTools, dotColor: '#f59e0b' },
};

const CONTENT_TYPE_OPTIONS = ['FAQ', 'GUIDE', 'TUTORIAL', 'TROUBLESHOOTING'];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toISOString().slice(0, 10);
  } catch {
    return '—';
  }
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const newAttachmentId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export default function SupportKnowledgeBase() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [editorId, setEditorId] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formCategory, setFormCategory] = useState('Time Tracking');
  const [formContentType, setFormContentType] = useState('FAQ');
  const [formContent, setFormContent] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [attachmentDragActive, setAttachmentDragActive] = useState(false);
  const attachmentInputRef = useRef(null);

  const loadPublished = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await graphqlRequest(SUPPORT_KB_ARTICLES_PUBLISHED_QUERY);
      const list = data?.supportKbArticlesPublished || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load knowledge base');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPublished();
  }, [loadPublished]);

  const articleModels = useMemo(() => {
    const mapped = (rows || []).map((r) => {
      return {
        id: r.id,
        title: r.title,
        category: r.category,
        typeKey: r.type || 'FAQ',
        author: '—',
        lastUpdated: r.updatedAt || r.createdAt || null,
        views: 0,
        content: r.content || '',
      };
    });

    mapped.sort((a, b) => new Date(b.lastUpdated || 0).getTime() - new Date(a.lastUpdated || 0).getTime());
    return mapped;
  }, [rows]);

  const tileCounts = useMemo(() => {
    const counts = {
      FAQ: 0,
      GUIDE: 0,
      TUTORIAL: 0,
      TROUBLESHOOTING: 0,
    };
    articleModels.forEach((a) => {
      const k = a.typeKey && counts[a.typeKey] !== undefined ? a.typeKey : 'FAQ';
      counts[k] += 1;
    });
    return counts;
  }, [articleModels]);

  const categoryOptions = useMemo(() => {
    const defaults = ['Time Tracking', 'Payments', 'Technical', 'Reports', 'Account Settings', 'Jobs & Assignments'];
    const apiCats = (rows || []).map((r) => String(r.category || '').trim()).filter(Boolean);
    const set = new Set([...defaults, ...apiCats]);
    return Array.from(set);
  }, [rows]);

  const recentArticles = useMemo(() => articleModels.slice(0, 6), [articleModels]);

  const resetEditor = useCallback(() => {
    setEditorId(null);
    setFormTitle('');
    setFormCategory('Time Tracking');
    setFormContentType('FAQ');
    setFormContent('');
    setAttachmentFiles([]);
    setAttachmentDragActive(false);
  }, []);

  const addAttachmentFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []).filter(Boolean);
    if (!incoming.length) return;
    setAttachmentFiles((prev) => {
      const existing = new Set(prev.map((p) => `${p.file.name}-${p.file.size}`));
      const next = [...prev];
      incoming.forEach((file) => {
        const key = `${file.name}-${file.size}`;
        if (existing.has(key)) return;
        existing.add(key);
        next.push({ id: newAttachmentId(), file });
      });
      return next;
    });
  }, []);

  const removeAttachment = useCallback((id) => {
    setAttachmentFiles((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const onAttachmentDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onAttachmentDragIn = useCallback((e) => {
    onAttachmentDrag(e);
    setAttachmentDragActive(true);
  }, [onAttachmentDrag]);

  const onAttachmentDragOut = useCallback((e) => {
    onAttachmentDrag(e);
    setAttachmentDragActive(false);
  }, [onAttachmentDrag]);

  const onAttachmentDrop = useCallback(
    (e) => {
      onAttachmentDrag(e);
      setAttachmentDragActive(false);
      addAttachmentFiles(e.dataTransfer?.files);
    },
    [addAttachmentFiles, onAttachmentDrag],
  );

  const openEdit = useCallback((article) => {
    setEditorId(article.id);
    setFormTitle(article.title || '');
    setFormCategory(article.category || 'Time Tracking');
    setFormContentType(article.typeKey);
    setFormContent(article.content || '');
    setAttachmentFiles([]);
    setAttachmentDragActive(false);
  }, []);

  const save = useCallback(
    async (targetStatus) => {
      const title = String(formTitle || '').trim();
      const content = String(formContent || '').trim();
      const category = String(formCategory || '').trim();
      const contentTypeKey = String(formContentType || 'FAQ').trim();

      if (!title) {
        toast.error('Title is required.');
        return;
      }
      if (!content) {
        toast.error('Content is required.');
        return;
      }
      if (!category) {
        toast.error('Category is required.');
        return;
      }

      try {
        if (editorId) {
          await graphqlRequest(UPDATE_SUPPORT_KB_ARTICLE_MUTATION, {
            input: {
              id: editorId,
              title,
              content,
              category,
              type: contentTypeKey,
              status: targetStatus,
            },
          });
        } else {
          await graphqlRequest(CREATE_SUPPORT_KB_ARTICLE_MUTATION, {
            input: {
              title,
              content,
              category,
              type: contentTypeKey,
              status: targetStatus,
            },
          });
        }

        await loadPublished();
        resetEditor();
      } catch (e) {
        toast.error(e?.message || 'Could not save article.');
      }
    },
    [editorId, formCategory, formContent, formContentType, formTitle, loadPublished, resetEditor],
  );

  const handlePublish = useCallback(() => save('PUBLISHED'), [save]);
  const handleSaveDraft = useCallback(() => save('DRAFT'), [save]);

  return (
    <div className="kb-page">
      <div className="kb-head">
        <div>
          <h1 className="kb-title">Knowledge Base</h1>
          <p className="kb-subtitle">Create and manage help articles, guides, and tutorials</p>
        </div>
      </div>

      {loadError ? <p className="kb-error">{loadError}</p> : null}

      <div className="kb-kpi-grid" role="list">
        {CONTENT_TYPE_OPTIONS.map((k) => {
          const def = ARTICLE_TYPE_MAP[k];
          const Icon = def.icon;
          return (
            <article key={k} className="kb-kpi-card" role="listitem">
              <div className="kb-kpi-icon" style={{ background: `${def.dotColor}20`, color: def.dotColor }}>
                <Icon aria-hidden />
              </div>
              <div className="kb-kpi-value">{loading ? '0' : tileCounts[k]}</div>
              <div className="kb-kpi-label">{def.label}</div>
            </article>
          );
        })}
      </div>

      <div className="kb-recent-card">
        <div className="kb-recent-head">
          <div>
            <h2 className="kb-section-title">Recent Articles</h2>
            <p className="kb-muted">Latest published knowledge base content</p>
          </div>
        </div>

        <div className="kb-table-wrap">
          <table className="kb-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Category</th>
                <th>Author</th>
                <th>Last updated</th>
                <th>Views</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="kb-muted">
                    Loading…
                  </td>
                </tr>
              ) : recentArticles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="kb-muted">
                    No articles yet (0 views).
                  </td>
                </tr>
              ) : (
                recentArticles.map((a) => (
                  <tr key={a.id}>
                    <td>{a.title}</td>
                    <td>
                      <span className="kb-type-pill">{ARTICLE_TYPE_MAP[a.typeKey].label}</span>
                    </td>
                    <td>{a.category}</td>
                    <td>{a.author}</td>
                    <td>{formatDate(a.lastUpdated)}</td>
                    <td>0</td>
                    <td>
                      <button type="button" className="kb-edit-btn" onClick={() => openEdit(a)}>
                        <FaEdit aria-hidden /> Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="kb-editor-card">
        <div className="kb-editor-head">
          <h2 className="kb-section-title">Article Editor</h2>
          <div className="kb-editor-actions">
            <button type="button" className="kb-secondary-btn" onClick={resetEditor}>
              New Article
            </button>
          </div>
        </div>

        <div className="kb-editor-grid">
          <label className="kb-field">
            <span className="kb-field-label">Title</span>
            <input
              type="text"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Enter article title"
            />
          </label>

          <label className="kb-field">
            <span className="kb-field-label">Content Type</span>
            <select
              value={formContentType}
              onChange={(e) => setFormContentType(e.target.value)}
            >
              {CONTENT_TYPE_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {ARTICLE_TYPE_MAP[k].label}
                </option>
              ))}
            </select>
          </label>

          <label className="kb-field">
            <span className="kb-field-label">Category</span>
            <select value={formCategory} onChange={(e) => setFormCategory(e.target.value)}>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className="kb-field kb-field--full">
            <span className="kb-field-label">Content</span>
            <textarea
              value={formContent}
              rows={7}
              onChange={(e) => setFormContent(e.target.value)}
              placeholder="Write your article content…"
            />
          </label>

          <div className="kb-field kb-field--full kb-attachments">
            <span className="kb-field-label">Attachments</span>
            <p className="kb-attachments-hint">
              Add screenshots, PDFs, or other files for reference. Selected files are not saved with the article yet.
            </p>
            <input
              ref={attachmentInputRef}
              type="file"
              className="kb-attachments-input"
              multiple
              onChange={(e) => {
                addAttachmentFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <div
              className={`kb-attachments-drop${attachmentDragActive ? ' kb-attachments-drop--active' : ''}`}
              onDragEnter={onAttachmentDragIn}
              onDragLeave={onAttachmentDragOut}
              onDragOver={onAttachmentDragIn}
              onDrop={onAttachmentDrop}
            >
              <div className="kb-attachments-drop-inner">
                <span className="kb-attachments-drop-icon" aria-hidden>
                  <FaPaperclip />
                </span>
                <div className="kb-attachments-drop-text">
                  <span className="kb-attachments-drop-title">Drop files here or browse</span>
                  <span className="kb-attachments-drop-sub">
                    PDF, images, Word, or other documents — multiple files allowed
                  </span>
                </div>
                <button
                  type="button"
                  className="kb-attachments-browse-btn"
                  onClick={() => attachmentInputRef.current?.click()}
                >
                  Add files
                </button>
              </div>
            </div>
            {attachmentFiles.length > 0 ? (
              <ul className="kb-attachments-list" aria-label="Selected attachments">
                {attachmentFiles.map(({ id, file }) => (
                  <li key={id} className="kb-attachments-chip">
                    <span className="kb-attachments-chip-name" title={file.name}>
                      {file.name}
                    </span>
                    <span className="kb-attachments-chip-meta">{formatFileSize(file.size)}</span>
                    <button
                      type="button"
                      className="kb-attachments-chip-remove"
                      onClick={() => removeAttachment(id)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <FaTimes aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>

        <div className="kb-editor-footer">
          <button type="button" className="kb-primary-btn" onClick={handlePublish}>
            Publish Article
          </button>
          <button type="button" className="kb-secondary-btn" onClick={handleSaveDraft}>
            Save as a Draft
          </button>
        </div>
      </div>
    </div>
  );
}
